import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE } from "@/config/fileUpload";
import { useMultipartUpload } from "@/hooks/useMultipartUpload";
import { useGenerateMultiplePreSignedPutUrl } from "@/mutations/useGenerateMultiplePreSignedPutUrl.ts";
import type { DocumentData } from "@/types/document.ts";
import { shouldUseMultipart } from "@/utils/multipartUpload.ts";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Divider, Grid2 as Grid, IconButton, LinearProgress, Stack } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import {
    type ReactNode,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useState,
} from "react";
import { type FileWithPath, useDropzone } from "react-dropzone";

export interface RhfDocumentsUploadRef {
    uploadDocuments: (tag?: string) => Promise<DocumentData[]>;
    getDocuments: () => DocumentData[];
    hasDocuments: () => boolean;
}

type Props = {
    caseUUID: string;
    maxFileSize?: number;
    acceptedTypes?: Record<string, string[]>;
    description?: string;
};

interface UploadItem {
    id: string;
    file: File;
    documentData: DocumentData;
    previewUrl: string | null;
    status: "pending" | "uploading" | "success" | "error";
    uploadProgress?: number; // Progress percentage (0-100)
}

const RhfDocumentsUpload = forwardRef<RhfDocumentsUploadRef, Props>(
    (
        {
            caseUUID,
            maxFileSize = MAX_FILE_SIZE,
            acceptedTypes = ACCEPTED_FILE_TYPES,
            description = "Note Upload",
        },
        ref,
    ): ReactNode => {
        const generatePreSignedPutUrl = useGenerateMultiplePreSignedPutUrl();
        const { uploadFileMultipart } = useMultipartUpload();

        const [uploadItems, setUploadItems] = useState<UploadItem[]>([]);
        const [uploading, setUploading] = useState(false);

        // Cleanup preview URLs on unmount or when items are removed
        useEffect(() => {
            return () => {
                uploadItems.forEach((item) => {
                    if (item.previewUrl) {
                        URL.revokeObjectURL(item.previewUrl);
                    }
                });
            };
        }, [uploadItems]);

        useImperativeHandle(
            ref,
            () => ({
                uploadDocuments: async (tag?: string): Promise<DocumentData[]> => {
                    const pendingItems = uploadItems.filter((item) => item.status === "pending");

                    if (pendingItems.length === 0) {
                        return [];
                    }

                    const documentsToUpload = pendingItems.map((item) => ({
                        ...item.documentData,
                        tag: tag || item.documentData.tag || "",
                    }));

                    setUploading(true);

                    try {
                        // Step 1: Generate pre-signed URLs
                        const urlResponse = await generatePresignedUrls(documentsToUpload);

                        if (!urlResponse?.uploads || urlResponse.uploads.length === 0) {
                            throw new Error("No upload URLs received");
                        }

                        // Step 2: Handle errors and filter to successful items
                        const successfulItems = await handleUrlGenerationResponse(
                            urlResponse,
                            pendingItems,
                            documentsToUpload,
                        );

                        if (successfulItems.length === 0) {
                            throw new Error("No valid upload URLs available");
                        }

                        // Step 3: Upload to S3
                        const uploadedDocuments = await uploadToS3(successfulItems);

                        // Step 4: Update status to success
                        setUploadItems((prev) =>
                            prev.map((item) => {
                                const wasUploaded = successfulItems.find((si) => si.id === item.id);
                                return wasUploaded ? { ...item, status: "success" as const } : item;
                            }),
                        );

                        return uploadedDocuments;
                    } catch (error) {
                        enqueueSnackbar("Document upload failed", { variant: "error" });

                        // Mark failed items as error
                        setUploadItems((prev) =>
                            prev.map((item) =>
                                item.status === "uploading"
                                    ? { ...item, status: "error" as const }
                                    : item,
                            ),
                        );

                        throw error;
                    } finally {
                        setUploading(false);
                    }
                },
                getDocuments: () => uploadItems.map((item) => item.documentData),
                hasDocuments: () => uploadItems.length > 0,
            }),
            [uploadItems, generatePreSignedPutUrl, uploadFileMultipart],
        );

        const generatePresignedUrls = async (
            documents: DocumentData[],
        ): Promise<typeof generatePreSignedPutUrl.data> => {
            return new Promise((resolve, reject) => {
                generatePreSignedPutUrl.mutate(documents, {
                    onSuccess: (data) => resolve(data),
                    onError: (error) => reject(error),
                });
            });
        };

        const handleUrlGenerationResponse = async (
            urlResponse: NonNullable<typeof generatePreSignedPutUrl.data>,
            pendingItems: UploadItem[],
            documentsToUpload: DocumentData[],
        ): Promise<Array<UploadItem & { uploadUrl: string; renamedFilename: string }>> => {
            // Report any errors
            if (urlResponse.errors && urlResponse.errors.length > 0) {
                urlResponse.errors.forEach((error) => {
                    if (error) {
                        enqueueSnackbar(`Error generating URL: ${error}`, { variant: "error" });
                    }
                });
            }

            // Match items with their upload URLs and updated document data (with tags)
            const successfulItems: Array<
                UploadItem & { uploadUrl: string; renamedFilename: string }
            > = [];

            for (let i = 0; i < pendingItems.length; i++) {
                const item = pendingItems[i];
                const updatedDoc = documentsToUpload[i]; // Get the version with the tag
                const upload = urlResponse.uploads.find(
                    (u) => u.originalFilename === item.documentData.filename,
                );

                if (upload?.url) {
                    successfulItems.push({
                        ...item,
                        documentData: updatedDoc, // Use the updated document data with tag
                        uploadUrl: upload.url,
                        renamedFilename: upload.filename,
                    });
                }
            }

            // Update items that failed to get URLs
            const successfulIds = new Set(successfulItems.map((item) => item.id));
            setUploadItems((prev) =>
                prev.map((item) =>
                    !successfulIds.has(item.id) && item.status === "pending"
                        ? { ...item, status: "error" as const }
                        : item,
                ),
            );

            return successfulItems;
        };

        const uploadToS3 = async (
            itemsToUpload: Array<UploadItem & { uploadUrl: string; renamedFilename: string }>,
        ): Promise<DocumentData[]> => {
            // Mark items as uploading
            const uploadingIds = new Set(itemsToUpload.map((item) => item.id));
            setUploadItems((prev) =>
                prev.map((item) =>
                    uploadingIds.has(item.id)
                        ? { ...item, status: "uploading" as const, uploadProgress: 0 }
                        : item,
                ),
            );

            const uploadPromises = itemsToUpload.map(async (item) => {
                try {
                    // Check if file should use multipart upload
                    if (shouldUseMultipart(item.file.size)) {
                        // Use multipart upload for large files
                        await uploadFileMultipart(
                            item.file,
                            {
                                idGlobalUUID: item.documentData.idGlobalUUID,
                                tag: item.documentData.tag || "Cases",
                                category: item.documentData.category,
                            },
                            (progress) => {
                                // Update progress for this specific item
                                setUploadItems((prev) =>
                                    prev.map((prevItem) =>
                                        prevItem.id === item.id
                                            ? { ...prevItem, uploadProgress: progress.percentage }
                                            : prevItem,
                                    ),
                                );
                            },
                        );
                    } else {
                        // Use regular upload for smaller files
                        const response = await fetch(item.uploadUrl, {
                            method: "PUT",
                            body: item.file,
                            headers: {
                                "Content-Type": item.file.type,
                            },
                        });

                        if (!response.ok) {
                            throw new Error(`Upload failed with status ${response.status}`);
                        }
                    }

                    // Return updated document data with renamed filename
                    return {
                        ...item.documentData,
                        filename: item.renamedFilename,
                        resourceName: `${item.documentData.resourceName.substring(
                            0,
                            item.documentData.resourceName.lastIndexOf("/"),
                        )}/${item.renamedFilename}`,
                    };
                } catch (error) {
                    enqueueSnackbar(`Failed to upload ${item.file.name}`, { variant: "error" });
                    throw error;
                }
            });

            const uploadedDocuments = await Promise.all(uploadPromises);

            enqueueSnackbar(`Successfully uploaded ${uploadedDocuments.length} document(s)`, {
                variant: "success",
            });

            return uploadedDocuments;
        };

        const onDrop = useCallback(
            (acceptedFiles: FileWithPath[]) => {
                if (acceptedFiles.length === 0) {
                    return;
                }

                const newItems: UploadItem[] = acceptedFiles.map((file) => {
                    const documentData: DocumentData = {
                        idGlobalUUID: caseUUID,
                        category: "Cases",
                        filename: file.name,
                        description: description,
                        size: file.size,
                        mime: file.type,
                        resourceName: `Cases/${caseUUID}/${file.name}`,
                        tag: null,
                    };

                    // Create preview URL for images
                    let previewUrl: string | null = null;
                    if (file.type.startsWith("image/")) {
                        previewUrl = URL.createObjectURL(file);
                    }

                    return {
                        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                        file,
                        documentData,
                        previewUrl,
                        status: "pending" as const,
                    };
                });

                setUploadItems((prev) => [...prev, ...newItems]);
            },
            [caseUUID, description],
        );

        const { getRootProps, getInputProps, isDragActive } = useDropzone({
            onDrop,
            multiple: true,
            accept: acceptedTypes,
            minSize: 0,
            maxSize: maxFileSize,
        });

        const handleDeleteDocument = (id: string) => {
            setUploadItems((prev) => {
                const itemToRemove = prev.find((item) => item.id === id);

                // Revoke preview URL before removing
                if (itemToRemove?.previewUrl) {
                    URL.revokeObjectURL(itemToRemove.previewUrl);
                }

                return prev.filter((item) => item.id !== id);
            });
        };

        const imageItems = uploadItems.filter((item) => item.previewUrl);
        const nonImageItems = uploadItems.filter((item) => !item.previewUrl);

        return (
            <Grid container spacing={2}>
                <Grid size={12} sx={{ p: 1 }}>
                    <Stack
                        {...getRootProps()}
                        sx={{
                            p: 1,
                            flexDirection: "column",
                            border: "1px dashed orange",
                            justifyContent: "center",
                        }}
                    >
                        <input {...getInputProps()} />
                        {/* Display images with previews */}
                        {imageItems.map((item) => (
                            <Box key={item.id} sx={{ position: "relative", mb: 1 }}>
                                <Box
                                    component="img"
                                    alt="Media"
                                    src={item.previewUrl || ""}
                                    sx={{
                                        height: "100%",
                                        width: "100%",
                                        opacity: item.status === "error" ? 0.5 : 1,
                                        border: item.status === "error" ? "2px solid red" : "none",
                                    }}
                                />
                                <IconButton
                                    aria-label="delete-media"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleDeleteDocument(item.id);
                                    }}
                                    sx={{
                                        position: "absolute",
                                        color: "#aaa",
                                        top: -8,
                                        right: 0,
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                                {item.status === "uploading" &&
                                    item.uploadProgress !== undefined && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                            }}
                                        >
                                            <LinearProgress
                                                variant="determinate"
                                                value={item.uploadProgress}
                                            />
                                        </Box>
                                    )}
                            </Box>
                        ))}
                        {/* Display non-image files */}
                        {nonImageItems.map((item) => (
                            <Box
                                key={item.id}
                                sx={{
                                    position: "relative",
                                    mb: 1,
                                    p: 1,
                                    bgcolor: "#f5f5f5",
                                    borderRadius: 1,
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Box sx={{ fontWeight: "bold" }}>{item.file.name}</Box>
                                        <Box sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                                            {(item.file.size / (1024 * 1024)).toFixed(2)} MB
                                        </Box>
                                    </Box>
                                    <IconButton
                                        aria-label="delete-file"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleDeleteDocument(item.id);
                                        }}
                                        size="small"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Stack>
                                {item.status === "uploading" &&
                                    item.uploadProgress !== undefined && (
                                        <Box sx={{ mt: 1 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={item.uploadProgress}
                                            />
                                            <Box
                                                sx={{
                                                    textAlign: "center",
                                                    fontSize: "0.75rem",
                                                    mt: 0.5,
                                                }}
                                            >
                                                {Math.round(item.uploadProgress)}%
                                            </Box>
                                        </Box>
                                    )}
                            </Box>
                        ))}
                        {uploadItems.length > 0 && <Divider />}
                        {uploadItems.length === 0 && isDragActive ? (
                            <Box sx={{ textAlign: "center", p: 1 }}>Drop the files here ...</Box>
                        ) : (
                            <Box sx={{ textAlign: "center", p: 1 }}>
                                Drag 'n' drop files here, or click to select files.
                            </Box>
                        )}
                    </Stack>
                    <Box sx={{ mt: 2 }}>
                        File size is limited to{" "}
                        {maxFileSize >= 1024 * 1024 * 1024
                            ? `${Math.round(maxFileSize / (1024 * 1024 * 1024))}GB`
                            : `${Math.round(maxFileSize / (1024 * 1024))}MB`}
                    </Box>
                    {uploading && (
                        <Stack
                            sx={{
                                flexDirection: "column",
                                justifyContent: "flex-start",
                                width: "100%",
                            }}
                        >
                            <Box sx={{ mt: 2 }}>Uploading documents to S3...</Box>
                            <LinearProgress />
                        </Stack>
                    )}
                </Grid>
            </Grid>
        );
    },
);

RhfDocumentsUpload.displayName = "RhfDocumentsUpload";

export default RhfDocumentsUpload;
